import React, { useState, useEffect, useCallback } from "react";
import PageWrapperForm from "../../components/PageWrapperForm";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";
import { method } from "lodash";
// ^ ASSUMPTION: adjust this import to wherever your fetchWrapper actually lives.
// It's expected to expose fetchWrapper.get(url) and fetchWrapper.post(url, formData),
// both already attaching auth headers and returning parsed JSON — mirroring how
// useTableDataWithPost calls the API in ExtraServiceManagementPage.

const API_BASE = "https://pro.mokshiq.in";

function formatMoney(n) {
  const v = Number.isFinite(n) ? n : 0;
  return `₹${v.toFixed(2)}`;
}

const SelfWalletTransferPage = () => {
const { userid, role, token, walletBal } = useSession();

  // ── State ────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDirection, setSelectedDirection] = useState(0); // 0 = W->M, 1 = M->W

  const [walletBalance, setWalletBalance] = useState(0);
  const [mainWallet, setMainWallet] = useState(0);
  const [creditWallet, setCreditWallet] = useState(0); // nwallet_ammount
  const [maxTransferable, setMaxTransferable] = useState(0);

  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [amountError, setAmountError] = useState("");

  const [toast, setToast] = useState(null); // { msg, isError }

  const showToast = useCallback((msg, isError = false) => {
    setToast({ msg, isError });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3200);
  }, []);

  // ── Load balances from profile API ──────────────────────────────────
  const loadBalances = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchWrapper(
        `${API_BASE}/get_user_profile?user=${encodeURIComponent(userid || "")}`
      );
      const data = res?.data;

      if (data) {
        const wb = Number(data.walletBalance ?? data.wallet_balance ?? 0) || 0;
        const mw = Number(data.nwalletbalance ?? data.nwallet_balance ?? 0) || 0;

        let credit = 0;
        const walletInfo = data.wallet_info;
        if (walletInfo && walletInfo.nwallet_ammount !== undefined) {
          const parsed = parseFloat(walletInfo.nwallet_ammount);
          credit = Number.isFinite(parsed) ? parsed : 0;
        }

        setWalletBalance(wb);
        setMainWallet(mw);
        setCreditWallet(credit);
        setMaxTransferable(Math.max(0, wb - credit));
      } else {
        showToast("Failed to load balances", true);
      }
    } catch (e) {
      showToast("Error connecting to server", true);
    } finally {
      setIsLoading(false);
    }
  }, [userid, showToast]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  // ── Derived display values ──────────────────────────────────────────
  const isW2M = selectedDirection === 0;
  const fromLabel = isW2M ? "Wallet Balance" : "Main Wallet";
  const toLabel = isW2M ? "Main Wallet" : "Wallet Balance";
  const fromBalance = isW2M ? walletBalance : mainWallet;
  const maxAllowed = isW2M ? maxTransferable : mainWallet;

  function validateAmount(value) {
    if (!value || !value.trim()) return "Please enter amount";
    const amt = parseFloat(value.trim());
    if (Number.isNaN(amt) || amt <= 0) return "Enter a valid amount";
    if (amt > maxAllowed) return `Amount exceeds maximum of ${formatMoney(maxAllowed)}`;
    return "";
  }

  function handleDirectionChange(idx) {
    setSelectedDirection(idx);
    setAmount("");
    setAmountError("");
  }

  // ── Transfer request ─────────────────────────────────────────────────
  const doTransfer = async (e) => {
    e.preventDefault();

    const err = validateAmount(amount);
    setAmountError(err);
    if (err) return;

    const amt = parseFloat(amount.trim());
    if (selectedDirection === 0 && amt > maxTransferable) {
      showToast(`Maximum transferable amount is ${formatMoney(maxTransferable)}`, true);
      return;
    }

    const action = selectedDirection === 0 ? "WALLET_TO_MAIN" : "MAIN_TO_WALLET";
    let    headers = {};
    const formdata = new FormData();
    formdata.append("action", action); // matches request.POST.get("action")
    formdata.append("amount", amount.trim());
    formdata.append("remark", remark.trim());

    setIsLoading(true);
    try {
        const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/update_user_kyc_nd_all`,{method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formdata,});
    const  jsonRes = await res.json();
      if (jsonRes?.status === true) {
        setAmount("");
        setRemark("");
        setAmountError("");
        showToast(jsonRes.message || "Transfer successful", false);
        await loadBalances();
      } else {
        showToast(jsonRes?.message || "Transfer failed", true);
      }
    } catch (e) {
      showToast("Failed to connect to server.", true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-3 px-md-4">
      <PageWrapperForm config={{ title: "Self Wallet Transfer" }}>
        {isLoading && walletBalance === 0 && mainWallet === 0 ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            {/* Balance cards */}
            <div className="row g-3 mb-4">
              <div className="col-6">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Wallet Balance</div>
                    <div className="fs-5 fw-bold text-primary">{formatMoney(walletBalance)}</div>
                    <div className="small text-warning">Credit: {formatMoney(creditWallet)}</div>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="text-muted small mb-1">Main Wallet</div>
                    <div className="fs-5 fw-bold text-info">{formatMoney(mainWallet)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transfer form */}
            <form onSubmit={doTransfer} className="card shadow-sm p-4">
              <h6 className="fw-bold mb-3">Transfer Funds</h6>

              {/* Direction toggle */}
              <div className="btn-group w-100 mb-3" role="group">
                <button
                  type="button"
                  className={`btn ${selectedDirection === 0 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleDirectionChange(0)}
                >
                  Wallet → Main
                </button>
                <button
                  type="button"
                  className={`btn ${selectedDirection === 1 ? "btn-primary" : "btn-outline-secondary"}`}
                  onClick={() => handleDirectionChange(1)}
                >
                  Main → Wallet
                </button>
              </div>

              {/* From / To */}
              <div className="d-flex align-items-center mb-3 gap-2">
                <span className="badge bg-primary-subtle text-primary flex-fill py-2">{fromLabel}</span>
                <span>→</span>
                <span className="badge bg-info-subtle text-info flex-fill py-2">{toLabel}</span>
              </div>

              {/* Available balance hint */}
              <div className="d-flex justify-content-between align-items-center bg-light rounded px-3 py-2 mb-2">
                <span className="text-muted small">Available from {fromLabel}</span>
                <span className="fw-bold text-primary small">{formatMoney(fromBalance)}</span>
              </div>

              {/* Credit warning, only for W -> M */}
              {isW2M && creditWallet > 0 && (
                <div className="alert alert-warning py-2 px-3 small mb-3">
                  Max transferable: {formatMoney(maxTransferable)} (credit used: {formatMoney(creditWallet)})
                </div>
              )}

              {/* Amount */}
              <div className="mb-3">
                <label className="form-label">Amount</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    className={`form-control ${amountError ? "is-invalid" : ""}`}
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      if (amountError) setAmountError("");
                    }}
                    onBlur={() => setAmountError(validateAmount(amount))}
                    placeholder="0.00"
                  />
                  {isW2M && (
                    <span className="input-group-text text-muted small">
                      Max {formatMoney(maxTransferable)}
                    </span>
                  )}
                </div>
                {amountError && <div className="text-danger small mt-1">{amountError}</div>}
              </div>

              {/* Remark */}
              <div className="mb-4">
                <label className="form-label">Remark (optional)</label>
                <input
                  type="text"
                  className="form-control"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                />
              </div>

              <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                {isLoading ? "Transferring..." : "Transfer Now"}
              </button>
            </form>
          </>
        )}
      </PageWrapperForm>

      {/* Toast */}
      {toast && (
        <div
          className={`position-fixed bottom-0 start-50 translate-middle-x mb-4 px-4 py-2 rounded text-white ${
            toast.isError ? "bg-danger" : "bg-success"
          }`}
          style={{ zIndex: 1080 }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default SelfWalletTransferPage;